import { TextField, Dropdown, AutoCompleteDropdown, DatePicker } from "app/components/CustomFormElements/syncFusionFormFields";
import { companyOverviewValidationSchema } from "app/helper/validationSchema/companyValidationSchema";
import { useFormik } from "formik";
import * as Yup from "yup";
import { actionError, actionSuccess, showLoader, hideLoader } from "middleware/actions/utilityAction";
import { showAlertBox } from "middleware/actions/alertBoxAction";
import {
  fetchSingleCompany,
  fetchCompanySource,
  fetchCompanyLogoUrl,
  fetchOverview,
  saveOverview,
  updateOverview,
} from "middleware/services/companyApi";
import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { useHistory } from "react-router-dom";
import { getLoggedInUser, getProductVariant, isAdmin } from "utilities";
import { PRODUCT_VARIANT, CONTENT_TYPE, SUB_CONTENT_TYPE, DELIVERY_TYPE } from "../../../../../../../constants";
import { companyEmployeeSize } from "../../../../constants";
import defaultCompanyLogo from "../../../../../../../assets/images/company.jpg";
import { Typography, Box } from "@mui/material";
import { FaArrowRight } from "react-icons/fa";
import { COMPANY_LOGO_IMG_SIZE } from "config";
import { fetchConfig } from "middleware/services/cmsApi";
import { useQuery } from "@tanstack/react-query";

const CompanyBasicOverview = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const productVariant = getProductVariant();
  const loggedInUser = getLoggedInUser();
  const companyImageTag = useRef(null);
  const companyImage = useRef(null);
  const [imageError, setImageError] = useState("");
  const [showTrash, setShowTrash] = useState(false);

  const [isDefaultImage, setIsDefaultImage] = useState(true);
  const [insiderCompanyContentType, setInsiderCompanyContentType] = useState([]);
  const [insiderCompanySubContentType, setInsiderCompanySubContentType] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [companyPaywallData, setCompanyPaywallData] = useState([]);
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyInformation, setCompanyInformation] = useState({
    companyName: "",
    companyFoundedYear: "",
    companyEmpSize: "",
    companyPublishedDate: new Date(),
    companyWebsite: "",
    companySocialLinkedIn: "",
    companySocialFacebook: "",
    companyDeliveryType: DELIVERY_TYPE.PREMIUM,
    companyContentType: CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES,
    companySubContentType: SUB_CONTENT_TYPE.COMPANY,
  });
  const [companySource, setCompanySource] = useState({
    totalCount: 0,
    data: [],
  });

  const normalizeEmployeeSizeText = (text) => {
    if (!text) return text;

    // Handle ranges like "1 to 10"
    const rangeMatch = text.match(/^(\d+)\s*to\s*(\d+)$/i);
    if (rangeMatch) {
      return `${rangeMatch[1]} - ${rangeMatch[2]}`;
    }

    // Handle formats like "10000 or more"
    const moreMatch = text.match(/^(\d+)\s*(\+|or more)$/i);
    if (moreMatch) {
      return `${moreMatch[1]}+`;
    }

    return text; // Return as-is if no match
  };

  const setSource = (elementName) => {
    const objectValues = ["companyParent"];

    const inputValue = document.querySelector(`input[name="${elementName}"]`)?.value;
    const checkedValue = document.querySelector(`input[name="${elementName}"]:checked`)?.value;

    // Special logic for companyEmpSize: always override with fallback
    if (elementName === "companyEmpSize") {
      const fallbackText = document.getElementById(`${elementName}Typography`)?.textContent?.trim();

      if (fallbackText) {
        formik.setFieldValue("companyEmpSize", normalizeEmployeeSizeText(fallbackText));
      }
      return;
    }

    if (inputValue && inputValue !== "on") {
      formik.setFieldValue(elementName, inputValue);
    } else if (checkedValue != null) {
      if (objectValues.includes(elementName)) {
        formik.setFieldValue(elementName, JSON.parse(checkedValue));
      } else {
        formik.setFieldValue(elementName, checkedValue);
      }
    } else {
      // Fallback: Get from dynamic Typography ID
      const fallbackElement = document.getElementById(`${elementName}Typography`);
      const fallbackText = fallbackElement?.textContent;

      if (fallbackText) {
        formik.setFieldValue(elementName, fallbackText.trim());
      }
    }
  };

  const { companyId } = props.computedMatch.params || {};

  const split_url = props.location?.pathname.split("/");
  const backPath = companyId ? `${split_url.slice(0, -2).join("/")}/edit#basic` : `${split_url.slice(0, -2).join("/")}/add#basic`;

  const formik = useFormik({
    initialValues: companyInformation,
    validationSchema: Yup.object().shape(companyOverviewValidationSchema),
  });

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: "Company", linkActive: false }];

    if (companyId) {
      links.push({
        linkUrl: `/company/${companyId}/edit#basic`,
        linkName: companyName,
        linkActive: false,
      });
    } else {
      links.push({
        linkUrl: `/company/add#basic`,
        linkName: "Add New",
        linkActive: false,
      });
    }

    links.push({
      linkUrl: companyId ? `/company/${companyId}/edit#basic` : `/company/add#basic`,
      linkName: "Basic",
      linkActive: false,
    });

    links.push({
      linkUrl: "",
      linkName: "Overview",
      linkActive: true,
    });

    return links;
  }, [companyId, companyName]);

  const getCompanyName = async () => {
    if (companyId) {
      const fields = { fields: ["id", "companyName"] };
      const response = await fetchSingleCompany(companyId, { ...fields });
      setCompanyName(response.data.data.companyName);
    }
  };

  const fetchCompanySourceData = async () => {
    if (companyId) {
      const param = [];
      param.push(["companyId", "eq", companyId]);
      dispatch(showLoader());
      try {
        const response = await fetchCompanySource(JSON.stringify(param));
        dispatch(hideLoader());

        if (response.status == 200) {
          setCompanySource(response.data);
        }
      } catch (e) {
        dispatch(hideLoader());
      }
    }
  };

  const convertDateFormat = (dateValue) => {
    // Ensure dateValue is a Date object
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

    // Check if date is valid
    if (isNaN(date)) {
      return "Invalid Date"; // Or throw an error if you prefer
    }

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, "0");
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    const time = "00:00:00 GMT"; // Fixed time

    const formattedDate = `${dayName}, ${day} ${monthName} ${year} ${time}`;
    console.log("formattedDate...", formattedDate);

    return formattedDate;
  };

  const fetchOverviewData = async () => {
    if (companyId) {
      dispatch(showLoader());
      const response = await fetchOverview(companyId);
      if (response) {
        const val = response.data.data;
        val.companyPaywallSetting && setCompanyPaywallData(val.companyPaywallSetting);
        let companyPaywallSetting = "NO";
        val.companyPaywallSetting &&
          val.companyPaywallSetting.map((elem) => {
            if (elem.productVariant == productVariant) {
              companyPaywallSetting = "YES";
            }
          });
        setCompanyInformation({
          companyName: val.companyName || companyInformation.companyName,
          companyFoundedYear: val.companyFoundedYear || "",
          companyEmpSize: normalizeEmployeeSizeText(val.companyEmpSize) || "",
          companyPublishedDate: val.productVariant.find((item) => item.productName === productVariant)?.lastPublishedDate || new Date(),
          companyWebsite: val.companyWebsite || "",
          companySocialLinkedIn: val.companySocial ? val.companySocial.linkedIn : "",
          companySocialFacebook: val.companySocial ? val.companySocial.facebook : "",
          companyDeliveryType: val.companyDeliveryType || DELIVERY_TYPE.PREMIUM,
          companyContentType: val.companyContentType || CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES,
          companySubContentType: val.companySubContentType || SUB_CONTENT_TYPE.COMPANY,
        });

        if (typeof val.companyLogo != "undefined" && val.companyLogo != "") {
          let img = await fetchCompanyLogoUrl(companyId, val.companyLogo);
          setCompanyLogo(img);
          setIsDefaultImage(false);
          setShowTrash(true);
        }
      }
      setSource("companyName");

      dispatch(hideLoader());
    }
  };

  // save a Overview
  const handleSaveOverview = async () => {
    if (isDefaultImage) {
      setImageError("Please upload image");
      dispatch(actionError("Please upload image"));
    }

    let companyPaywallProductPlatform = productVariant == "Cognition" ? "Signal V2" : `${productVariant} V2`;
    if (formik.values?.companyPaywallSetting == "YES") {
      const index = companyPaywallData && companyPaywallData?.findIndex((object) => object?.productPlatform === companyPaywallProductPlatform);
      if (index === -1) {
        companyPaywallData.push({
          productVariant: productVariant,
          productPlatform: companyPaywallProductPlatform,
          taxonomyTagging: [],
        });
      }
    }
    let overviewData = {
      companyName: formik.values.companyName,
      companyFoundedYear: formik.values.companyFoundedYear,
      companyEmpSize: formik.values.companyEmpSize,
      companySocial: {
        twitter: formik.values.companySocialTwitter,
        facebook: formik.values.companySocialFacebook,
        linkedIn: formik.values.companySocialLinkedIn,
      },
      companyEmail: formik.values.companyEmail,
      companyWebsite: formik.values.companyWebsite,
      companyWorkInProgress: formik.values.companyWorkInProgress,
      companyPaywallSetting: companyPaywallData,
      companyPublishedDate: convertDateFormat(formik.values.companyPublishedDate),
    };

    overviewData.companyParent = {};
    let saveOverviewData = new FormData();
    for (let uKey in overviewData) {
      if (typeof overviewData[uKey] != "object") {
        if (overviewData[uKey] && overviewData[uKey] !== "") saveOverviewData.append(uKey, overviewData[uKey]);
      } else {
        if (
          overviewData[uKey].acquiredBy &&
          (overviewData[uKey].acquiredBy?.companyId == overviewData[uKey].acquiredBy?.companyName ||
            overviewData[uKey].acquiredBy?.companyName.includes("Create"))
        ) {
          const value = {
            value: "Acquired By",
            acquiredBy: {
              companyName: overviewData[uKey].acquiredBy.companyId,
            },
          };
          saveOverviewData.append(uKey, JSON.stringify(value));
        } else {
          if (overviewData[uKey] && overviewData[uKey] !== "") saveOverviewData.append(uKey, JSON.stringify(overviewData[uKey]));
          if (uKey == "companyLegalStatus" && !overviewData[uKey]) saveOverviewData.append(uKey, "");
        }
      }
    }

    if (companyImage.current.files.length && typeof companyImage.current.files[0] != "undefined") {
      saveOverviewData.append("companyLogo", companyImage.current.files[0]);
    } else if (companyLogo == undefined) {
      saveOverviewData.append("companyLogo", "");
    }
    if (productVariant == PRODUCT_VARIANT.INSIDER) {
      saveOverviewData.append("companyDeliveryType", formik.values.companyDeliveryType || DELIVERY_TYPE.PREMIUM);
      saveOverviewData.append("companyContentType", formik?.values?.companyContentType || CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES);
      saveOverviewData.append("companySubContentType", formik?.values?.companySubContentType || SUB_CONTENT_TYPE.COMPANY);
    }
    if (!saveOverviewData.companyFoundedYear) saveOverviewData.append("companyFoundedYear", "");
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).map((field) => {
          touched[field] = true;
          return touched;
        });
        const err =
          res &&
          Object.values(res).map((e) => {
            return e;
          });

        formik.setFormikState({ ...formik, touched: touched, errors: res });
        if (err) {
          console.error(err);
          dispatch(actionError("" + err.join(" . ")));
        }
      } else {
        if (props.location.pathname == "/processing/company/add") {
          const workflowPayload = isAdmin()
            ? { allocationQueue: "PROCESSING_FRESH" }
            : {
                userId: loggedInUser._id,
                allocationQueue: "PROCESSING_FRESH",
              };
          saveOverviewData.append("workflow", JSON.stringify(workflowPayload));
        }
        if (props.location.pathname == "/data-qc/company/add") {
          const workflowPayload = isAdmin() ? { allocationQueue: "QC_FRESH" } : { userId: loggedInUser._id, allocationQueue: "QC_FRESH" };
          saveOverviewData.append("workflow", JSON.stringify(workflowPayload));
        }
        if (props.location.pathname == "/company/add") {
          const workflowPayload = isAdmin()
            ? { allocationQueue: "PROCESSING_FRESH" }
            : {
                userId: loggedInUser._id,
                allocationQueue: "PROCESSING_FRESH",
              };
          saveOverviewData.append("workflow", JSON.stringify(workflowPayload));
        }
        !isDefaultImage && imageError === "" && saveOverviewCall(saveOverviewData);
      }
    });
  };

  // save a Overview
  const saveOverviewCall = async (saveOverviewData) => {
    dispatch(showLoader());
    if (Boolean(companyId)) {
      saveOverviewData.append("selectedProductVariant", getProductVariant());
      await updateOverview(companyId, saveOverviewData)
        .then((response) => {
          dispatch(hideLoader());
          dispatch(actionSuccess("Company Updated Successfully"));
          history.push(backPath);
        })
        .catch((err) => {
          console.error(err);
          dispatch(actionError(err?.data?.message || "Something went wrong"));
        });
    } else {
      try {
        const productVariant = getProductVariant();
        if (productVariant) saveOverviewData.append("productVariant", JSON.stringify([{ productName: productVariant, isLive: "NO" }]));
        saveOverviewData.append("userId", loggedInUser._id);
        saveOverviewData.append("companyCreatedByUserId", loggedInUser._id);
        saveOverviewData.append("companyCreatedByUserName", loggedInUser.user_fname + " " + loggedInUser.user_lname);
        const response = await saveOverview(companyId, saveOverviewData);
        fetchOverviewData();
        dispatch(hideLoader());
        if (response.status == 200) {
          if (typeof response.data.data.id != "undefined") {
            dispatch(actionSuccess("Company Info Saved Successfully"));
            history.push(`/company/${response.data.data.id}/edit#basic`);
            props.newRecord();
          }
        }
      } catch (e) {
        dispatch(hideLoader());
        if (e.status == 409) {
          dispatch(actionError(e.data.message));
        }
      }
    }
  };

  const { data: deliveryTypeData } = useQuery({
    queryKey: ["delivery-type-config"],
    queryFn: () => {
      let params = {};
      params.filters = [["configName", "eq", "delivery_type"]];
      return fetchConfig(params);
    },
    suspense: false,
    enabled: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchIntervalInBackground: false,
  });

  const deliveryTypeDataSource = useMemo(() => {
    return deliveryTypeData?.data?.data?.[0]?.configValue || [];
  }, [deliveryTypeData]);

  const readCompanyImage = (imageFile) => {
    let reader = new FileReader();
    reader.onload = (e) => {
      companyImageTag.current.setAttribute("src", e.target.result);
    };
    reader.readAsDataURL(imageFile);
  };

  const fetchContentType = async () => {
    const contentType = JSON.parse(localStorage.getItem("contentType")) || [];
    if (contentType?.length > 0) {
      const resData = contentType?.map((v) => {
        if (v.contentType === CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES) {
          return { label: v.contentType, value: v.contentType, subContentType: v.subContentType };
        }
      });
      if (resData) {
        let data = resData.filter((item) => typeof item == "object");
        setInsiderCompanyContentType(data);
      } else {
        setInsiderCompanyContentType([]);
        setInsiderCompanySubContentType([]);
      }
    }
  };

  useEffect(() => {
    if (!formik?.values?.companyContentType) return;
    let subContentType = insiderCompanyContentType?.find((content) => content?.value === formik?.values?.companyContentType)?.subContentType;
    if (subContentType?.length > 0) {
      const resData = subContentType?.map((v) => {
        return { label: v.contentType, value: v.contentType };
      });
      resData.sort((a, b) => a.value.localeCompare(b.value));
      setInsiderCompanySubContentType(resData || []);
    } else {
      setInsiderCompanySubContentType([]);
    }
  }, [insiderCompanyContentType, formik?.values?.companyContentType]);

  useEffect(() => {
    formik.setValues({
      companyName: companyInformation.companyName,
      companyFoundedYear: companyInformation.companyFoundedYear,
      companyEmpSize: companyInformation.companyEmpSize,
      companyPublishedDate: companyInformation.companyPublishedDate,
      companyWebsite: companyInformation.companyWebsite,
      companySocialLinkedIn: companyInformation.companySocialLinkedIn,
      companySocialFacebook: companyInformation.companySocialFacebook,
      companyDeliveryType: companyInformation.companyDeliveryType,
      companyContentType: companyInformation.companyContentType,
      companySubContentType: companyInformation.companySubContentType,
    });
  }, [companyInformation]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
  }, [dispatch, breadCrumbLinks]);

  useEffect(() => {
    if (companyId != "") {
      getCompanyName();
      fetchCompanySourceData();
      fetchOverviewData();
    }
  }, [companyId]);

  useEffect(() => {
    fetchContentType();
  }, []);

  return (
    <div className="p-0 mx-3 company-basic-modal">
      <div className="modal-header">Company - Overview</div>
      <div className="modal-body">
        <div className="row no-gutters border" style={{ borderBottom: "2px solid #dee2e6" }}>
          {/* Left Section */}
          <div className="col-4 p-3" style={{ borderRight: "2px solid #dee2e6", backgroundColor: "#F2F2F2" }}>
            <h5
              style={{
                fontFamily: "Arial",
                fontWeight: 700,
                fontSize: "14px",
                backgroundColor: "transparent",
                color: "black",
              }}
            >
              Original Data From Sources
            </h5>
          </div>

          {/* Right Section */}
          <div className="col-8 p-3" style={{ backgroundColor: "#F2F2F2" }}>
            <h5
              style={{
                fontFamily: "Arial",
                fontWeight: 700,
                fontSize: "14px",
                backgroundColor: "transparent",
                color: "black",
              }}
            >
              Company Basic Information
            </h5>
            {/* Add your form or details here */}
          </div>
        </div>

        {/* Content Row */}
        <div className="row no-gutters model-body-content">
          {/* Left Content */}
          <div className="col-4 p-3" style={{ borderRight: "2px solid #dee2e6" }}>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>Company Name</Typography>
                      <Typography
                        id="companyNameTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companyName}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companyName) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companyName) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companyName")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>Founded Year</Typography>
                      <Typography
                        id="companyFoundedYearTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companyFoundedYear}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companyFoundedYear) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companyFoundedYear) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companyFoundedYear")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>Employee Size</Typography>
                      <Typography
                        id="companyEmpSizeTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companyEmpSize}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companyEmpSize) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companyEmpSize) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companyEmpSize")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>Website</Typography>
                      <Typography
                        id="companyWebsiteTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companyWebsite}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companyWebsite) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companyWebsite) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companyWebsite")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>LinkedIn</Typography>
                      <Typography
                        id="companySocialLinkedInTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companySocial.linkedIn}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companySocial.linkedIn) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companySocial.linkedIn) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companySocialLinkedIn")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
            <div className={`FilterList mainFilterDiv`}>
              <div style={{ display: "flex", maxHeight: "120px" }}>
                <div className={"p-3"}>
                  <div className={"title_top"}>
                    <Box>
                      <Typography>Facebook</Typography>
                      <Typography
                        id="companySocialFacebookTypography"
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                          marginTop: "10px",
                        }}
                        style={{ fontWeight: 700 }}
                      >
                        {companySource?.data[0]?.companySocial.facebook}
                      </Typography>
                    </Box>
                  </div>
                </div>
                <div
                  style={{
                    cursor: Boolean(companySource?.data[0]?.companySocial.facebook) ? "pointer" : "default",
                    opacity: Boolean(companySource?.data[0]?.companySocial.facebook) ? 1 : "0.25",
                    marginTop: "20px",
                  }}
                  className="draggable-content-card-arrow"
                  onClick={() => setSource("companySocialFacebook")}
                >
                  <FaArrowRight size={30} style={{ height: "16px", width: "16px", color: "#4B4B55 !important" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="col-8 p-3">
            {/* Add your form or details here */}
            <div className="row">
              <div className="col-md-12">
                <div className="pb-0">
                  <TextField
                    formik={formik}
                    type="text"
                    id="companyName"
                    placeholder="Company Name *"
                    name="companyName"
                    numberCount={true}
                    maxLength={125}
                    characterLimitText={true}
                  />
                </div>
              </div>
            </div>
            <div className="row pt-4">
              <div className="col-md-4">
                <div className="form-group">
                  <TextField formik={formik} type="text" id="companyFoundedYear" placeholder="Founded Year" name="companyFoundedYear" />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <Dropdown
                    formik={formik}
                    sortOrder="Acending"
                    placeholder="Select employee size"
                    id="companyEmpSize"
                    name="companyEmpSize"
                    fields={{ text: "label", value: "value" }}
                    dataSource={companyEmployeeSize}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <DatePicker
                    formik={formik}
                    properties={{
                      placeholder: "Publish Date *",
                      id: "companyPublishedDate",
                      name: "companyPublishedDate",
                      format: "dd MMM yyyy",
                      allowEdit: false,
                      floatLabelType: "Auto",
                      openOnFocus: true,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="row pt-2">
              <div className="col-md-4">
                <div className="form-group">
                  <TextField formik={formik} id="companyWebsite" placeholder="Company Website *" name="companyWebsite" type="text" />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <TextField
                    formik={formik}
                    id="companySocialLinkedIn"
                    placeholder="Company LinkedIn Page URL"
                    name="companySocialLinkedIn"
                    type="url"
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <TextField
                    formik={formik}
                    id="companySocialFacebook"
                    placeholder="Company Facebook Page URL"
                    name="companySocialFacebook"
                    type="url"
                  />
                </div>
              </div>
            </div>
            {productVariant == PRODUCT_VARIANT.INSIDER && (
              <div className="row pt-2">
                <div className="col-md-4">
                  {deliveryTypeDataSource?.length > 0 && (
                    <Dropdown
                      formik={formik}
                      placeholder="Delivery Type *"
                      required="required"
                      id="companyDeliveryType"
                      name="companyDeliveryType"
                      fields={{ text: "label", value: "value" }}
                      value={formik?.values?.companyDeliveryType || ""}
                      dataSource={deliveryTypeDataSource}
                    />
                  )}
                </div>
                <div className="col-md-4">
                  {insiderCompanySubContentType?.length > 0 && (
                    <Dropdown
                      formik={formik}
                      placeholder="Sub Content Type *"
                      required="required"
                      id="companySubContentType"
                      name="companySubContentType"
                      fields={{ text: "label", value: "value" }}
                      value={formik?.values?.companySubContentType || ""}
                      dataSource={insiderCompanySubContentType}
                    />
                  )}
                </div>
              </div>
            )}

            <div className="row pt-2 pl-3">
              <div className="col-md-12">
                <div className="row mb-3" style={{ fontSize: "16px", fontWeight: 700, color: "#4B4B55" }}>
                  Upload Company Logo *
                </div>
                <div className="mb-4">
                  <div className="row">
                    <div className="product-logo-holder p-0">
                      <div>
                        <img
                          src={companyLogo || defaultCompanyLogo}
                          alt="Company Logo"
                          width="200"
                          height="200"
                          ref={companyImageTag}
                          // style={{ position: "relative"}}
                        />
                      </div>
                      <div className="product-logo-action" style={{ top: "2px", right: "6px" }}>
                        <span
                          onClick={() => {
                            dispatch(
                              showAlertBox({
                                content: "Are you sure, you want to delete?",
                                okCallback: async () => {
                                  setShowTrash(false);
                                  setImageError("Please upload image");
                                  companyImageTag.current.setAttribute("src", defaultCompanyLogo);
                                  companyImage.current.value = "";
                                },
                              })
                            );
                          }}
                        >
                          <i className={`fa fa-trash mr-1 ${!showTrash ? "d-none" : undefined}`} aria-hidden="true" />
                        </span>
                        <span onClick={() => companyImage.current.click()}>
                          <i className="fa fa-pencil-alt" aria-hidden="true"></i>
                        </span>
                      </div>
                      <input
                        type="file"
                        className="d-none"
                        accept=".jpg, .png, .jpeg"
                        id="companyLogo"
                        name="companyLogo"
                        ref={companyImage}
                        formik={formik}
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            if (
                              (e.target.files[0].type == "image/png" ||
                                e.target.files[0].type == "image/jpg" ||
                                e.target.files[0].type == "image/jpeg") &&
                              e.target.files[0].size < COMPANY_LOGO_IMG_SIZE
                            ) {
                              setImageError("");
                              setIsDefaultImage(false);
                              readCompanyImage(e.target.files[0]);
                            } else {
                              // companyImageTag.current.setAttribute("src", companyImage);
                              setImageError("File must be JPG/JPEG or PNG format and size must not exceed 10 MB.");
                            }
                          } else {
                            setImageError("Please upload image");
                            // companyImageTag.current.setAttribute("src", companyImage);
                          }
                        }}
                      />
                    </div>
                    <div className="ml-3">
                      <p className="mb-0">Note:</p>
                      <ul className="ml-4">
                        <li>Only JPG/JPEG or PNG files are allowed.</li>
                        <li>Max file size allowed is 10MB.</li>
                      </ul>
                    </div>
                  </div>
                  <div
                    className="d-flex align-item-center justify-content-start text-danger"
                    style={{ display: imageError ? "block" : "none", marginLeft: "-1rem" }}
                    role="alert"
                  >
                    <small>{imageError}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-right border-top" style={{ padding: ".8rem" }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3"
          >
            Cancel
          </button>

          <button type="button" className="btn btn-primary cy-btn" onClick={handleSaveOverview}>
            Save Overview
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyBasicOverview;
